/*
 * Copyright (c) 2026, Amblique
 * All rights reserved.
 */

import BaseImageGallery from '@salesforce/retail-react-app/app/theme/components/project/image-gallery';

/** PDP hero gallery aspect on mobile (125:83) — height from CSS only, no JS remeasure on scroll. */
export const GALLERY_HERO_ASPECT_MOBILE = '125 / 83';

const HERO_IMAGE_MAX_WIDTH_LG = '52.5rem';

const GALLERY_VIDEO_MAX_H = {
  base: '100%',
  md: '34.875rem',
};


const VIDEO_CONTROL_INSET = 5;
const VIDEO_CONTROL_INSET_BOTTOM = {
  base: 5,
  md: 8,
};

const GALLERY_VIDEO = {
  backdropBlur: 'blur(7px)',
  iconOuter: 8,
  iconPadding: 2,
  iconSvg: 4,
};

const VIDEO_GALLERY_ICON_BUTTON = {
  alignItems: 'center',
  backdropFilter: GALLERY_VIDEO.backdropBlur,
  bg: 'brandColors.greyscale20',
  borderRadius: 'base',
  color: 'white',
  display: 'flex',
  flexShrink: 0,
  h: GALLERY_VIDEO.iconOuter,
  justifyContent: 'center',
  height: GALLERY_VIDEO.iconOuter,
  minWidth: GALLERY_VIDEO.iconOuter,
  p: GALLERY_VIDEO.iconPadding,
  w: GALLERY_VIDEO.iconOuter,
  _active: {bg: 'brandColors.greyscale40'},
  _hover: {bg: 'brandColors.greyscale30'},
  _focusVisible: {
    boxShadow: 'none',
    outline: 'auto',
    outlineOffset: '1px',
    zIndex: 2,
  },
  sx: {
    '& svg': {
      color: 'white',
      flexShrink: 0,
      h: GALLERY_VIDEO.iconSvg,
      w: GALLERY_VIDEO.iconSvg,
    },
  },
};

const ImageGallery = {
  ...BaseImageGallery,
  sizes: {
    ...BaseImageGallery.sizes,
    md: {
      ...BaseImageGallery.sizes.md,
      heroImage: {
        maxWidth: ['none', 'none', 'none', HERO_IMAGE_MAX_WIDTH_LG],
      },
      heroImageSkeleton: {
        maxWidth: ['none', 'none', 'none', HERO_IMAGE_MAX_WIDTH_LG],
      },
    },
  },
  parts: [
    ...(BaseImageGallery.parts || []),
    'videoSlide',
    'videoMuteControl',
    'videoPlayPauseControl',
    'videoMuteButton',
    'videoPlayPauseButton',
    'cylindoViewerWrapper',
    'cylindoOverlayLeft',
    'cylindoDragToRotateBadge',
    'cylindoDragToRotateIcon',
    'cylindoDragToRotateText',
    'cylindoOverlayRight',
    'cylindoArButton',
    'cylindoFullscreenButton',
    'cylindoFullscreenIcon',
    'cylindoZoomButton',
    'cylindoZoomIcon',
    'cylindoArModalOverlay',
    'cylindoArModalContent',
    'cylindoArModalBackButton',
    'cylindoArModalBackButtonIcon',
    'cylindoArModalBackButtonLabel',
    'cylindoArModalBody',
    'cylindoArModalStack',
    'cylindoArModalTitle',
    'cylindoArModalDescription',
    'cylindoArModalDeviceIcons',
    'cylindoArQrContainer',
    'cylindoArQrImage',
    'cylindoArModalFooter',
    'cylindoArButtonCompact',
    'placeholderWrapper',
    'placeholderImage',
    'heroSwiper',
  ],
  baseStyle: {
    ...BaseImageGallery.baseStyle,

    /**
     * PDP gallery height:
     * - Mobile: fixed aspect 125:83 (no --hero-gallery-slide-min-h — avoids swiper.update jump on scroll).
     * - Desktop (xl+): JS unified tallest slide via --hero-gallery-slide-min-h (cap 600px).
     */
    heroSwiper: {
      width: '100%',
      sx: {
        '& .swiper-wrapper': {
          maxHeight: 'var(--hero-gallery-slide-max-h, 600px)',
          aspectRatio: {base: GALLERY_HERO_ASPECT_MOBILE, xl: 'unset'},
          minHeight: {
            base: 'unset',
            xl: 'var(--hero-gallery-slide-min-h, 600px)',
          },
        },
        '& .swiper': {
          touchAction: 'pan-x pan-y pinch-zoom',
        },
        '& .swiper-slide img': {
          touchAction: 'pan-x pan-y pinch-zoom',
          maxHeight: 'var(--hero-gallery-slide-max-h, 600px)',
        },
        '& .swiper-slide': {
          alignItems: 'stretch',
          aspectRatio: {base: GALLERY_HERO_ASPECT_MOBILE, xl: 'unset'},
          display: 'flex',
          minHeight: {
            base: 'unset',
            xl: 'var(--hero-gallery-slide-min-h, calc(100vw * 12 / 25))',
          },
          width: '100%',
        },
        '& .swiper-slide > *': {
          alignSelf: 'stretch',
          flex: '1 1 auto',
          minH: 0,
          minW: 0,
          width: '100%',
          minHeight: {
            base: '100%',
            xl: 'var(--hero-gallery-slide-min-h, calc(100vw * 12 / 25))',
          },
        },
      },
    },

    videoSlide: {
      alignItems: 'center',
      bg: 'black',
      display: 'flex',
      height: '100%',
      justifyContent: 'center',
      minHeight: '100%',
      position: 'relative',
      width: '100%',
      sx: {
        '& .amb-video': {
          alignSelf: 'center',
          maxHeight: GALLERY_VIDEO_MAX_H,
          maxWidth: '100%',
          width: '100%',
        },

        '& svg': {
          width: 5,
          height: 5,
        },

        '& .amb-video.landscape iframe, & .amb-video.landscape video, & .amb-video.portrait iframe, & .amb-video.portrait video': {
          pointerEvents: 'none',
          height: '100%',
          left: '0',
          width: '100%',
        },
      },
    },

    videoMuteControl: {
      bottom: VIDEO_CONTROL_INSET_BOTTOM,
      left: VIDEO_CONTROL_INSET,
      position: 'absolute',
      zIndex: 2,
    },

    videoPlayPauseControl: {
      bottom: VIDEO_CONTROL_INSET_BOTTOM,
      position: 'absolute',
      right: VIDEO_CONTROL_INSET,
      zIndex: 2,
    },

    videoMuteButton: {
      ...VIDEO_GALLERY_ICON_BUTTON,
    },

    videoPlayPauseButton: {
      ...VIDEO_GALLERY_ICON_BUTTON,
    },
    cylindoViewerWrapper: {
      position: 'relative',
    },
    cylindoOverlayLeft: {
      left: {base: 2.25, lg: '0'},
      pointerEvents: 'none',
      position: 'absolute',
      top: {base: 3, lg: '0'},
      zIndex: 2,
    },
    cylindoDragToRotateBadge: {
      alignItems: 'center',
      display: 'inline-flex',
      gap: 2,
      height: 8,
      py: 1.5,
    },
    cylindoDragToRotateIcon: {
      h: 4.25,
      w: 5,
    },
    cylindoDragToRotateText: {
      color: 'brandColors.greyscale90',
      fontFamily: {base: 'body', lg: 'heading'},
      fontSize: {base: '1xs', lg: 'xs'},
      fontStyle: 'normal',
      fontWeight: {base: 400, lg: 600},
      lineHeight: 'shorter',
      textAlign: 'center',
    },
    cylindoOverlayRight: {
      flexDirection: 'row',
      position: 'absolute',
      right: {base: 2.25, lg: '0'},
      top: {base: 3, lg: '0'},
      zIndex: 2,
    },
    cylindoArButton: {
      alignItems: 'center',
      bg: 'transparent',
      border: '1px solid',
      borderColor: 'brandColors.greyscale10',
      borderRadius: '4px',
      color: 'brandColors.greyscale90',
      display: 'flex',
      fontFamily: {base: 'body', lg: 'heading'},
      fontSize: {base: '1xs', lg: 'xs'},
      fontWeight: {base: 400, lg: 600},
      gap: 2,
      h: { base: 8, md: 8 },
      minW: { base: 'auto', md: 'auto' },
      justifyContent: 'center',
      margin: 0,
      lineHeight: 1.2,
      px: 3,
      py: 1.5,
      position: 'relative',
      zIndex: 1,
      _focusVisible: {
        boxShadow: 'none',
        outline: 'auto',
        outlineOffset: '2px',
        zIndex: 2,
      },
      sx: {
        '& .chakra-button__icon': {
          marginLeft: 0,
        },
        '& > svg, & .chakra-button__icon svg': {
          h: 4,
          w: 4,
          strokeWidth: '1px',
        },
      },
    },
    cylindoFullscreenButton: {
      bg: 'transparent',
      border: '1px solid',
      borderColor: 'brandColors.greyscale10',
      borderRadius: '4px',
      bottom: {base: 3, lg: '0'},
      boxShadow: 'sm',
      color: 'gray.800',
      display: {base: 'inline-flex', xl: 'none'},
      h: 8,
      minW: 8,
      position: 'absolute',
      right: {base: 2.25, lg: '0'},
      w: 8,
      zIndex: 2,
    },
    cylindoFullscreenIcon: {
      color: 'gray.800',
      h: 3.25,
      w: 3.25,
    },
    cylindoZoomButton: {
      alignItems: 'center',
      bg: 'transparent',
      border: '1px solid',
      borderColor: 'brandColors.greyscale10',
      borderRadius: '4px',
      display: 'flex',
      gap: 2,
      height: 8,
      minWidth: 8,
      fontSize: 'xs',
      justifyContent: 'center',
      position: 'relative',
      px: 2,
      py: 1.5,
      zIndex: 1,
      _focusVisible: {
        boxShadow: 'none',
        outline: 'auto',
        outlineOffset: '2px',
        zIndex: 2,
      },
    },
    cylindoZoomIcon: {
      color: 'brandColors.greyscale80',
      h: 4.75,
      w: 4.75,
      stroke: 'brandColors.greyscale80',
      strokeWidth: '1px',
    },
    cylindoArModalOverlay: {
      backdropFilter: 'none',
      bg: 'transparent',
    },
    cylindoArModalContent: {
      backgroundColor: 'white',
      border: 'none',
      borderRadius: 0,
      boxShadow: 'none',
      display: 'flex',
      flexDirection: 'column',
      h: 'calc(100dvh - var(--header-height, 0px) - var(--header-menu-extra, 0px))',
      left: 0,
      margin: 0,
      maxH: 'calc(100dvh - var(--header-height, 0px) - var(--header-menu-extra, 0px))',
      maxW: '100vw',
      minH: 'calc(100dvh - var(--header-height, 0px) - var(--header-menu-extra, 0px))',
      mt: 0,
      mx: 0,
      my: 0,
      overflow: 'hidden',
      p: 0,
      position: 'fixed',
      px: 0,
      py: 0,
      right: 0,
      top: 'calc(var(--header-height, 0px) + var(--header-menu-extra, 0px) - 1px)',
      transform: 'none',
      width: '100vw',
      sx: {
        boxShadow: 'none !important',
        outline: 'none',
        transform: 'none !important',
      },
    },
    cylindoArModalBackButton: {
      alignItems: 'center',
      backgroundColor: 'rgba(243, 243, 243, 0.5)',
      border: 'none',
      borderRadius: '3px',
      boxShadow: 'none',
      color: '#4d4d4d',
      display: 'flex',
      flexDirection: 'row',
      fontFamily: 'Montserrat, system-ui, sans-serif',
      fontStyle: 'normal',
      gap: '0',
      h: '30px',
      justifyContent: 'flex-start',
      left: 'auto',
      lineHeight: '30px',
      minH: '30px',
      minW: '164.5px',
      opacity: 0.9,
      p: 0,
      position: 'absolute',
      right: '15px',
      textAlign: 'left',
      top: '10px',
      w: 'auto',
      zIndex: 3,
      _active: {backgroundColor: 'rgba(230, 230, 230, 0.75)'},
      _focusVisible: {boxShadow: 'none', outline: '2px solid', outlineColor: 'brandColors.greyscale40', outlineOffset: '2px'},
      _hover: {backgroundColor: 'rgba(243, 243, 243, 0.75)', opacity: 1},
      sx: {
        '&.chakra-button': {
          fontWeight: 500,
          letterSpacing: 'normal',
          minHeight: '30px',
          height: '30px',
          textTransform: 'none',
          verticalAlign: 'middle',
        },
        '&.chakra-button:not([data-loading])': {
          paddingInlineEnd: 0,
          paddingInlineStart: 0,
        },
      },
    },
    cylindoArModalBackButtonIcon: {
      display: 'inline-block',
      flexShrink: 0,
      h: '24px',
      m: '4px 7px 4px 13px',
      w: '24px',
    },
    cylindoArModalBackButtonLabel: {
      color: 'inherit',
      flex: 1,
      fontFamily: 'inherit',
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '30px',
      minW: 0,
      overflow: 'hidden',
      textAlign: 'left',
      marginRight: '10px',
    },
    cylindoArModalBody: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'center',
      minH: 0,
      overflow: 'auto',
      overflowX: 'hidden',
      p: 0,
      pb: {base: 6, md: 8},
      position: 'relative',
      pt: {base: 10, md: 12},
      px: {base: 5, md: 8},
      zIndex: 1,
    },
    cylindoArModalStack: {
      alignItems: 'center',
      justifyContent: 'center',
      maxW: '100%',
      w: '100%',
      sx: {
        gap: '63px',
      },
    },
    cylindoArModalTitle: {
      color: 'brandColors.greyscale90',
      fontSize: '30px',
      fontWeight: 700,
      letterSpacing: 'headingTight',
      lineHeight: 'short',
      maxW: 'lg',
      pb: 0,
      textAlign: 'center',
    },
    cylindoArModalDescription: {
      color: '#4d4d4d',
      flexShrink: 0,
      fontSize: '14px',
      fontWeight: 100,
      lineHeight: 'tall',
      maxW: 'md',
      pb: 0,
      textAlign: 'center',
    },
    cylindoArModalDeviceIcons: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      justifyContent: 'center',
      pb: 0,
      sx: {
        '& img': {
          height: 'auto',
          maxHeight: '48px',
          maxWidth: '100%',
          width: 'auto',
        },
      },
    },
    cylindoArQrContainer: {
      alignItems: 'center',
      backgroundColor: 'transparent',
      boxShadow: 'none',
      display: 'flex',
      flexShrink: 0,
      height: '165px',
      justifyContent: 'center',
      mx: 'auto',
      p: '15px',
      position: 'relative',
      width: '165px',
    },
    cylindoArQrImage: {
      height: 'auto',
      maxH: '100%',
      maxW: '100%',
      objectFit: 'contain',
      width: 'auto',
    },
    cylindoArModalFooter: {
      color: 'brandColors.greyscale70',
      flexShrink: 0,
      fontSize: 'xs',
      fontWeight: 400,
      lineHeight: 'short',
      maxW: 'sm',
      mt: 0,
      mx: 'auto',
      pb: 2,
      position: 'relative',
      textAlign: 'center',
    },
    // Compact AR button (when no buttonProps from parent)
    cylindoArButtonCompact: {
      bg: 'white',
      border: '1px solid',
      borderColor: 'gray.200',
      boxShadow: 'sm',
      color: 'gray.800',
      fontWeight: 500,
      h: 10,
    },

    placeholderWrapper: {
      alignItems: 'center',
      aspectRatio: 256 / 123,
      bg: 'primary.paleLinen',
      display: 'flex',
      justifyContent: 'center',
      maxWidth: ['none', 'none', 'none', HERO_IMAGE_MAX_WIDTH_LG],
      width: '100%',
    },

    placeholderImage: {
      height: 'auto',
      maxHeight: '75%',
      maxWidth: '75%',
      objectFit: 'contain',
      userSelect: 'none',
    },
  },
};

export default ImageGallery;
